import { PermissionAccessProvider, PermissionAwareTextarea } from '../../../contexts/PermissionAccessContext';
import type { IncidentDetail } from '../../../types/safetySystem';
import { DateField, DateTimeField, LookupSelectField, Section, SelectField, TextField, YesNoField, inputClass, str } from './unitFields';
import { lookupOptions } from './unitForm';
import { ReadOnly, fmtDateTime } from './safetyDrawerShared';
import {
  LOAD_EMPTY_OPTIONS, PREVENTABLE_OPTIONS, alcoholTestPrompt, dateSet, drugTestPrompt, fromDateTimeInput, money, toDateTimeInput,
} from './incidentForm';

/**
 * Incident tab — V1 incident form, two columns. Left: when / who / what / where (lookups for
 * type, root cause, department, accountability group, state). Right: record stamps, the
 * close date (setting it closes the incident; the server validates the closing checklist),
 * the Summary card from `detail.summary`, and the DOT / drug & alcohol test fields. The
 * D&A timing prompts (V1 chkdatestreason) reveal a reason textarea whose text is posted as
 * `noshow.{drug|alcohol|alcohollate}` (and kept on the matching reason column).
 */

export interface NoshowReasons { drug: string; alcohol: string; alcohollate: string }

export interface IncidentInfoTabProps {
  detail: IncidentDetail;
  incident: Record<string, any>;
  onChange: (key: string, value: unknown) => void;
  noshow: NoshowReasons;
  onNoshowChange: (key: keyof NoshowReasons, value: string) => void;
  isNew: boolean;
  canEdit: boolean;
  portalContainer: HTMLElement | null;
}

const ReasonPrompt = ({ label, hint, value, onChange }: { label: string; hint: string; value: string; onChange: (v: string) => void }) => (
  <div className="col-span-2 rounded-md border border-amber-400 bg-amber-50 p-2 text-sm dark:border-amber-600 dark:bg-amber-950">
    <div className="mb-1 font-medium">{hint}</div>
    <PermissionAwareTextarea aria-label={label} value={value} rows={2} onChange={(e) => onChange(e.target.value)} className={inputClass} />
  </div>
);

export default function IncidentInfoTab({ detail, incident, onChange, noshow, onNoshowChange, isNew, canEdit, portalContainer }: IncidentInfoTabProps) {
  const t = (key: string) => (v: unknown) => onChange(key, v);
  const dt = (key: string) => (v: string) => onChange(key, fromDateTimeInput(v));
  const lk = detail.lookups;
  const states = (lk.states ?? []).map((s) => ({ value: s.recno, label: `${s.recno} - ${s.label}` }));
  const summary = detail.summary;
  const drug = drugTestPrompt(incident);
  const alcohol = alcoholTestPrompt(incident);
  const closing = dateSet(incident.incidentclosedate);

  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="space-y-3">
          <Section title="Incident">
            <DateTimeField label="Incident Date/Time" value={toDateTimeInput(incident.incidentdatetime)} onChange={dt('incidentdatetime')} />
            <TextField label="Reported By" value={incident.incidentreportedby} onChange={t('incidentreportedby')} />
            <LookupSelectField label="Incident Type" value={Number(incident.incidenttype) > 0 ? incident.incidenttype : ''} onChange={t('incidenttype')}
              options={lookupOptions(lk.incidenttypes)} search />
            <TextField label="GWI Claim #" value={incident.gwiclaimnum} onChange={t('gwiclaimnum')} maxLength={30} />
            <YesNoField label="Bobtail" value={incident.isbobtail} onChange={t('isbobtail')} />
            <SelectField label="Loaded / Empty" value={incident.loadempty} onChange={t('loadempty')} options={LOAD_EMPTY_OPTIONS} />
            <TextField label="Pro #" value={Number(incident.pronumber) > 0 ? incident.pronumber : ''} onChange={(v) => onChange('pronumber', v === '' ? 0 : v)} type="number" />
            <TextField label="OFI Rec #" value={Number(incident.ofirecno) > 0 ? incident.ofirecno : ''} onChange={(v) => onChange('ofirecno', v === '' ? 0 : v)} type="number" />
            <LookupSelectField label="Root Cause" value={Number(incident.incidentrootcause) > 0 ? incident.incidentrootcause : ''} onChange={t('incidentrootcause')}
              options={lookupOptions(lk.rootcauses)} search />
            <LookupSelectField label="Department" value={Number(incident.incidentdept) > 0 ? incident.incidentdept : ''} onChange={t('incidentdept')}
              options={lookupOptions(lk.departments)} search />
            <LookupSelectField label="Accountability Group" value={Number(incident.accountability_group) > 0 ? incident.accountability_group : ''}
              onChange={t('accountability_group')} options={lookupOptions(lk.accountability_groups)} search />
            <TextField label="Total Claim Reserves" value={incident.totalclaimreserves} onChange={t('totalclaimreserves')} type="number" />
          </Section>
          <Section title="Location">
            <TextField label="Location" value={incident.incidentloc} onChange={t('incidentloc')} className="col-span-2" />
            <TextField label="Cross Street" value={incident.crossstreet} onChange={t('crossstreet')} className="col-span-2" />
            <TextField label="Address" value={incident.incidentadd} onChange={t('incidentadd')} className="col-span-2" />
            <TextField label="City" value={incident.incidentcity} onChange={t('incidentcity')} />
            <SelectField label="State" value={incident.incidentstate} onChange={t('incidentstate')} options={states} search />
            <TextField label="Zip" value={incident.incidentzip} onChange={t('incidentzip')} maxLength={10} />
          </Section>
          <Section title="Police">
            <YesNoField label="Police Notified" value={incident.policenotified} onChange={t('policenotified')} />
            <TextField label="Report #" value={incident.incidentreportnum} onChange={t('incidentreportnum')} />
            <TextField label="Officer #" value={incident.officernum} onChange={t('officernum')} />
            <TextField label="County" value={incident.officercounty} onChange={t('officercounty')} />
            <TextField label="Precinct" value={incident.officerprecinct} onChange={t('officerprecinct')} />
          </Section>
          <Section title="Description">
            <div className="col-span-2">
              <PermissionAwareTextarea aria-label="Description" rows={5} className={inputClass} value={str(incident.incidentdescription)}
                onChange={(e) => onChange('incidentdescription', e.target.value)} />
            </div>
          </Section>
        </div>

        <div className="space-y-3">
          <Section title="Record">
            <ReadOnly label="Created By" value={detail.incident?.createdby} />
            <ReadOnly label="Created" value={fmtDateTime(detail.incident?.createdatetime)} />
            <ReadOnly label="Last Changed By" value={detail.incident?.lastchangedby} />
            <ReadOnly label="Last Changed" value={fmtDateTime(detail.incident?.lastchangedatetime)} />
            <ReadOnly label="Submitted" value={isNew ? '' : fmtDateTime(detail.incident?.incidentsubmitdate)} />
            <ReadOnly label="Status" value={isNew ? 'NEW' : str(detail.incident?.incidentstatus)} />
            <DateField label="Close Date" value={incident.incidentclosedate} onChange={t('incidentclosedate')} portalContainer={portalContainer} className="col-span-2" />
            {closing && !dateSet(detail.incident?.incidentclosedate) && (
              <div className="col-span-2 text-xs text-amber-800 dark:text-amber-200">Saving with a close date closes the incident; the closing checklist is verified on save.</div>
            )}
          </Section>
          <Section title="Summary">
            <ReadOnly label="Costs" value={money(summary?.costs)} />
            <ReadOnly label="Reimbursements" value={money(summary?.reimbursements)} />
            <ReadOnly label="Chargeback" value={money(summary?.chargeback)} />
            <ReadOnly label="Out of Pocket" value={money(summary?.out_of_pocket)} />
            <ReadOnly label="Injuries" value={summary?.injuries ?? 0} />
            <ReadOnly label="Fatalities" value={summary?.fatalities ?? 0} />
            <ReadOnly label="Towed" value={summary?.towed ?? 0} />
          </Section>
          <Section title="DOT / Testing">
            <YesNoField label="DOT Recordable" value={incident.dotrecordable} onChange={t('dotrecordable')} />
            <YesNoField label="DA Tests Required" value={incident.datestrequired} onChange={t('datestrequired')} />
            <YesNoField label="Drug Test Taken" value={incident.drugtesttaken} onChange={t('drugtesttaken')} />
            <DateTimeField label="Drug Test Completed" value={toDateTimeInput(incident.drugtestcompletedatetime)} onChange={dt('drugtestcompletedatetime')} />
            {drug === 'noshow' && (
              <ReasonPrompt label="Drug test no-show reason" value={noshow.drug} onChange={(v) => onNoshowChange('drug', v)}
                hint="The drug test was completed more than 32 hours after the incident. Enter the no-show reason (a NO-SHOW FORM should be attached)." />
            )}
            <YesNoField label="Alcohol Test Taken" value={incident.alcoholtesttaken} onChange={t('alcoholtesttaken')} />
            <DateTimeField label="Alcohol Test Completed" value={toDateTimeInput(incident.alcoholtestcompletedatetime)} onChange={dt('alcoholtestcompletedatetime')} />
            {alcohol === 'noshow' && (
              <ReasonPrompt label="Alcohol test no-show reason" value={noshow.alcohol} onChange={(v) => onNoshowChange('alcohol', v)}
                hint="The alcohol test was completed more than 8 hours after the incident. Enter the no-show reason (a NO-SHOW FORM should be attached)." />
            )}
            {alcohol === 'late' && (
              <ReasonPrompt label="Alcohol test late reason" value={noshow.alcohollate} onChange={(v) => onNoshowChange('alcohollate', v)}
                hint="The alcohol test was completed more than 2 hours after the incident. Enter the reason it was late (a CHAIN OF CUSTODY document should be attached)." />
            )}
            <YesNoField label="Statement Received" value={incident.statementrcvd} onChange={t('statementrcvd')} />
            <SelectField label="Preventable" value={incident.preventable} onChange={t('preventable')} options={PREVENTABLE_OPTIONS} />
          </Section>
        </div>
      </div>
    </PermissionAccessProvider>
  );
}
